import type { AnalyticsContext, Locale, LocationId } from "./types";
import { ACTIVE_BRAND, isConsultFree } from "./brands";
import type { LocationDisplay } from "./brands/types";

/** Backend base URL. Dev -> emulator (.env.development); prod -> deployed functions. */
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://us-central1-clinical-workflow-bondortho.cloudfunctions.net";

/** reCAPTCHA v3 site key. Empty -> "dev-bypass" token (emulator skips check). */
export const RECAPTCHA_SITE_KEY: string =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) ?? "";

/** Active brand identity used across the UI (logo, contact, calendar invite, theme). */
export const BRAND = {
  slug: ACTIVE_BRAND.slug,
  name: ACTIVE_BRAND.brandName,
  phone: ACTIVE_BRAND.phone,
  phoneHref: ACTIVE_BRAND.phoneHref,
  logo: ACTIVE_BRAND.logo,
  theme: ACTIVE_BRAND.theme,
  /** Set on brands that hand off to Ortho2's booking site (outage mitigation). */
  ortho2BookingUrl: ACTIVE_BRAND.ortho2BookingUrl,
  /** False on brands that charge for the consult (Beglin) - drives the copy. */
  consultIsFree: isConsultFree(ACTIVE_BRAND),
  /** Per-locale fee disclaimer for mostly-free brands with a priced exception (Adventure). */
  consultFeeNotice: ACTIVE_BRAND.consultFeeNotice,
};

export type { LocationDisplay };

/** Offices for the active brand, keyed by backend locationId. */
export const LOCATIONS: Record<LocationId, LocationDisplay> = ACTIVE_BRAND.locations;

export const LOCATION_IDS = Object.keys(LOCATIONS) as LocationId[];

/** Locales the active brand supports; index 0 is the default (drives the language toggle). */
export const LOCALES: Locale[] = ACTIVE_BRAND.locales;

export interface EmbedParams {
  locale: Locale;
  /** Preselected location, or null to show the picker. */
  location: LocationId | null;
  /** Parent origin for postMessage targeting; "*" when unknown. */
  parentOrigin: string;
  /** Configurable conversion value for the Ads/GA event. */
  conversionValue: number;
  /** Rendered inside the embed popup modal (reserve space for its close X). */
  popup: boolean;
  analytics: AnalyticsContext;
}

/** Resolve a locale string to one the active brand supports, else the brand default. */
function detectLocale(explicit: string | null): Locale {
  const supported = ACTIVE_BRAND.locales;
  if (explicit && supported.includes(explicit as Locale)) return explicit as Locale;
  const nav = (typeof navigator !== "undefined" ? navigator.language || "" : "").toLowerCase();
  const guess: Locale = nav.startsWith("es") ? "es" : "en";
  return supported.includes(guess) ? guess : supported[0];
}

/** Resolve a ?location= value to a brand locationId: exact registry key or a brand alias. */
function asLocationId(v: string | null): LocationId | null {
  if (!v) return null;
  const up = v.toUpperCase();
  if (LOCATIONS[up]) return up;
  return ACTIVE_BRAND.locationAliases?.[up] ?? null;
}

/** Parse the iframe URL query string that embed.js populates. */
export function parseEmbedParams(): EmbedParams {
  const q = new URLSearchParams(window.location.search);
  const analytics: AnalyticsContext = {
    gclid: q.get("gclid") || undefined,
    gbraid: q.get("gbraid") || undefined,
    wbraid: q.get("wbraid") || undefined,
    utm_source: q.get("utm_source") || undefined,
    utm_medium: q.get("utm_medium") || undefined,
    utm_campaign: q.get("utm_campaign") || undefined,
    utm_term: q.get("utm_term") || undefined,
    utm_content: q.get("utm_content") || undefined,
    referrer: q.get("referrer") || undefined,
  };
  const explicitLocation = asLocationId(q.get("location"));
  // Single-office brands auto-select their lone office (skip the 1-item picker).
  const location = explicitLocation ?? (LOCATION_IDS.length === 1 ? LOCATION_IDS[0] : null);
  return {
    locale: detectLocale(q.get("locale")),
    location,
    parentOrigin: q.get("origin") || "*",
    conversionValue: Number(q.get("cv") || "0") || 0,
    popup: q.get("popup") === "1",
    analytics,
  };
}
